import { Button } from '@optools/components/ui/button';
import { DataCentersAndChannels } from '@optools/tools/partner/components/data-centers-and-channels';
import { MappingConfirmationDialog } from '@optools/tools/partner/components/mapping-confirmation-dialog';
import { PartnersAndTargets } from '@optools/tools/partner/components/partners-and-targets';
import { useChannelMappingSelection } from '@optools/tools/partner/hooks/useChannelMappingSelection';
import { usePartnerData } from '@optools/tools/partner/hooks/usePartnerData';
import { useTargetMappingSelection } from '@optools/tools/partner/hooks/useTargetMappingSelection';
import { querySchema } from '@optools/tools/partner/schemas/mapping';
import { createFileRoute, stripSearchParams, useNavigate, useSearch } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { isEqual } from 'es-toolkit';
import { useEffect, useState } from 'react';

/* eslint-disable perfectionist/sort-objects */
export const Route = createFileRoute('/_auth/partner/mapping')({
  component: RouteComponent,
  validateSearch: zodValidator(querySchema),
  search: { middlewares: [stripSearchParams({ channel: '', target: '' })] },
});
/* eslint-enable perfectionist/sort-objects */

export function RouteComponent() {
  const { channel: channelParam, target: targetParam } = useSearch({
    from: '/_auth/partner/mapping',
  });
  const navigate = useNavigate({ from: Route.fullPath });

  const { channelMap, dataCenters, partners, targetMap } = usePartnerData();

  // Shared by both mapping modes for default data-center tab hints.
  const [activeDataCenterTab, setActiveDataCenterTab] = useState<string | null>(null);

  // GET_TARGET drives channel-tab hints; reset when switching targets so tabs don’t leak across targets.
  useEffect(() => {
    setActiveDataCenterTab(null);
  }, [targetParam]);

  // Selection state and loading/ref semantics differ by URL mode (`channel` vs `target` search param).
  const {
    channelQueryLoading,
    currentChannel,
    currentChannelTargetIds,
    handleSelectTarget,
    selectedTargetIds,
  } = useChannelMappingSelection({
    activeDataCenterTab,
    channelMap,
    channelParam,
    dataCenters,
    setActiveDataCenterTab,
  });

  const {
    activePartnerTab,
    currentTarget,
    currentTargetChannelId,
    handleSelectChannel,
    selectedChannelId,
    targetQueryLoading,
  } = useTargetMappingSelection({
    activeDataCenterTab,
    dataCenters,
    partners,
    setActiveDataCenterTab,
    targetMap,
    targetParam,
  });

  const mappingQueriesLoading = channelQueryLoading || targetQueryLoading;

  const clearButtonDisabled = !(targetParam || channelParam);
  const confirmButtonDisabled = (() => {
    if (channelParam) return isEqual(currentChannelTargetIds, selectedTargetIds);
    if (targetParam) return currentTargetChannelId === selectedChannelId;
    return true;
  })();

  return (
    <section className="flex h-full flex-col gap-4">
      <div className="flex flex-1 flex-col gap-4">
        <DataCentersAndChannels
          activeTab={activeDataCenterTab}
          currentChannelId={currentChannel?.id}
          loading={channelQueryLoading}
          selectedChannelId={selectedChannelId}
          onSelectChannel={handleSelectChannel}
        />
        <PartnersAndTargets
          activeTab={activePartnerTab}
          currentTargetId={currentTarget?.id}
          loading={targetQueryLoading}
          selectedTargets={selectedTargetIds}
          onSelectTarget={handleSelectTarget}
        />
      </div>

      <footer className="-mb-4 -ml-4 flex w-[calc(100%+theme(space.8))] items-center justify-center bg-muted p-2">
        <div className="flex gap-2">
          <Button
            className="w-36"
            disabled={clearButtonDisabled}
            variant="outline"
            onClick={() => navigate({ search: { channel: '', target: '' } })}>
            Clear
          </Button>
          <MappingConfirmationDialog
            currentChannel={currentChannel}
            currentChannelTargetIds={currentChannelTargetIds}
            currentTarget={currentTarget}
            currentTargetChannelId={currentTargetChannelId}
            disabled={confirmButtonDisabled}
            loading={mappingQueriesLoading}
            selectedChannelId={selectedChannelId}
            selectedTargetIds={selectedTargetIds}
          />
        </div>
      </footer>
    </section>
  );
}
